<template>
  <Head title="AI Prompt Generator" />

  <!-- Loading state while getting fingerprint -->
  <div v-if="isInitializing" class="flex items-center justify-center h-full w-full bg-white">
    <div class="text-center">
      <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600 mb-2"></div>
      <p class="text-sm text-gray-600">Загрузка...</p>
    </div>
  </div>

  <div v-else class="flex flex-col h-full w-full bg-white overflow-hidden">
    <!-- Header -->
    <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0 w-full">
      <div class="flex items-center gap-2 min-w-0">
        <div class="w-6 h-6 flex items-center justify-center text-violet-600">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
        <span class="text-sm font-medium text-gray-900">Генератор промптов</span>
      </div>
      <button
        @click="clearChat"
          class="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        Новый
      </button>
    </div>

    <!-- Messages Area -->
    <div class="flex-1 overflow-y-auto overflow-x-hidden w-full min-h-0" ref="messagesContainer">
      <div class="max-w-3xl mx-auto px-4 py-6">
        <!-- Welcome Message -->
        <div v-if="messages.length === 0" class="text-center py-12">
          <div class="mb-4">
            <svg class="w-12 h-12 mx-auto text-violet-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 class="text-xl font-semibold text-gray-900 mb-2">Создайте промпт для изображения</h2>
          <p class="text-gray-500 text-sm">Опишите, какое изображение вы хотите создать</p>
        </div>

        <!-- Messages -->
        <div v-for="(message, index) in messages" :key="index" class="mb-6">
          <PromptMessage :message="message" @copy="copyToClipboard" />
        </div>

        <!-- Loading Indicator -->
        <div v-if="isLoading" class="mb-6">
          <div class="flex gap-3">
            <div class="flex-shrink-0">
              <div class="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center">
                <svg class="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
            <div class="flex-1">
              <div class="flex gap-1 py-3">
                <div class="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style="animation-delay: 0ms"></div>
                <div class="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style="animation-delay: 150ms"></div>
                <div class="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style="animation-delay: 300ms"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Settings Area -->
    <div class="border-t border-gray-200 bg-gray-50 px-4 py-3 flex-shrink-0">
      <div class="max-w-3xl mx-auto flex flex-wrap gap-3">
        <!-- Platform selector -->
        <div class="flex-1 min-w-[140px]">
          <label class="block text-xs text-gray-500 mb-1">Платформа</label>
          <select
            v-model="selectedPlatform"
            class="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-300"
          >
            <option value="nano_banana">Nano Banana</option>
            <option value="chatgpt">ChatGPT Image</option>
            <option value="midjourney">Midjourney</option>
            <option value="universal">Универсальный</option>
          </select>
        </div>

        <!-- Style selector -->
        <div class="flex-1 min-w-[140px]">
          <label class="block text-xs text-gray-500 mb-1">Стиль</label>
          <select
            v-model="selectedStyle"
            class="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-300"
          >
            <option value="photorealistic">Фотореализм</option>
            <option value="anime">Аниме</option>
            <option value="3d">3D рендер</option>
            <option value="watercolor">Акварель</option>
            <option value="illustration">Иллюстрация</option>
            <option value="oil_painting">Масляная живопись</option>
            <option value="sketch">Скетч</option>
            <option value="cinematic">Кинематографичный</option>
          </select>
        </div>

        <!-- Aspect ratio selector -->
        <div class="flex-1 min-w-[100px]">
          <label class="block text-xs text-gray-500 mb-1">Размер</label>
          <select
            v-model="selectedRatio"
            class="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-300"
          >
            <option value="1:1">1:1</option>
            <option value="16:9">16:9</option>
            <option value="9:16">9:16</option>
            <option value="4:3">4:3</option>
            <option value="3:2">3:2</option>
            <option value="2:3">2:3</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Input Area -->
    <div class="border-t border-gray-200 bg-white flex-shrink-0 w-full">
      <div class="max-w-3xl mx-auto px-4 py-4">
        <form @submit.prevent="sendMessage" class="flex items-center gap-2">
          <div class="relative flex-1">
            <textarea
              v-model="inputMessage"
              @keydown.enter.exact.prevent="sendMessage"
              placeholder="Опишите желаемое изображение..."
              rows="1"
              class="w-full px-4 py-3 pr-12 text-gray-900 bg-gray-100 border-0 rounded-2xl resize-none focus:outline-none focus:ring-2 focus:ring-violet-300 max-h-48 overflow-y-auto block"
              style="word-break: break-word;"
              ref="textareaRef"
            ></textarea>
            <button
              type="submit"
              :disabled="!inputMessage.trim() || isLoading"
              class="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-white rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              :class="inputMessage.trim() && !isLoading ? 'bg-violet-600 hover:bg-violet-700' : 'bg-gray-400'"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18" />
              </svg>
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Copy notification -->
    <div
      v-if="showCopyNotification"
      class="fixed bottom-24 left-1/2 -translate-x-1/2 bg-gray-900 text-white px-4 py-2 rounded-lg text-sm shadow-lg z-50"
    >
      Промпт скопирован!
    </div>
  </div>
</template>

<script setup>
import { ref, nextTick, onMounted, onUnmounted, watch } from 'vue'
import { Head } from '@inertiajs/vue3'
import PromptMessage from './PromptMessage.vue'
import { getUserFingerprint } from '../../lib/fingerprint.js'
import { createConsumer } from '@rails/actioncable'

const messages = ref([])
const inputMessage = ref('')
const isLoading = ref(false)
const messagesContainer = ref(null)
const textareaRef = ref(null)
const tempChatId = ref(null)
const userId = ref(null)
const isInitializing = ref(true)
const cable = ref(null)
const subscription = ref(null)
const typingIntervalId = ref(null)
const showCopyNotification = ref(false)

// Settings
const selectedPlatform = ref('midjourney')
const selectedStyle = ref('photorealistic')
const selectedRatio = ref('1:1')

// Auto-resize textarea
watch(inputMessage, () => {
  nextTick(() => {
    if (textareaRef.value) {
      textareaRef.value.style.height = 'auto'
      textareaRef.value.style.height = textareaRef.value.scrollHeight + 'px'
    }
  })
})

// Scroll to bottom when new messages arrive
watch(messages, () => {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    }
  })
}, { deep: true })

// Subscribe/unsubscribe to ActionCable when chat is opened/closed
watch(tempChatId, (newTempChatId, oldTempChatId) => {
  if (oldTempChatId && subscription.value) {
    console.log('Unsubscribing from widget_chat_' + oldTempChatId)
    subscription.value.unsubscribe()
    subscription.value = null
  }

  if (newTempChatId) {
    console.log('Subscribing to widget_chat_' + newTempChatId)
    subscribeToChat(newTempChatId)
  }
})

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text)
    showCopyNotification.value = true
    setTimeout(() => {
      showCopyNotification.value = false
    }, 2000)
  } catch (err) {
    console.error('Failed to copy:', err)
  }
}

const animateTyping = (fullText) => {
  if (typingIntervalId.value) {
    clearInterval(typingIntervalId.value)
    typingIntervalId.value = null
  }

  isLoading.value = false

  const messageIndex = messages.value.length
  const newMessage = {
    role: 'assistant',
    content: '',
    timestamp: new Date()
  }
  messages.value.push(newMessage)

  let currentIndex = 0
  const charsPerInterval = 5
  const intervalMs = 30

  typingIntervalId.value = setInterval(() => {
    if (currentIndex < fullText.length) {
      currentIndex += charsPerInterval
      messages.value[messageIndex].content = fullText.substring(0, currentIndex)
    } else {
      clearInterval(typingIntervalId.value)
      typingIntervalId.value = null
    }
  }, intervalMs)
}

const subscribeToChat = (chatId) => {
  if (!cable.value) {
    cable.value = createConsumer()
  }

  subscription.value = cable.value.subscriptions.create(
    {
      channel: 'WidgetChatChannel',
      temp_chat_id: chatId,
      fingerprint: userId.value
    },
    {
      connected() {
        console.log('Connected to WidgetChatChannel for chat ' + chatId)
      },
      disconnected() {
        console.log('Disconnected from WidgetChatChannel')
      },
      received(data) {
        console.log('Received message from ActionCable:', data)

        if (data.type === 'new_message' && data.message) {
          const fullText = data.message.content || data.message.text
          animateTyping(fullText)
        }
      }
    }
  )
}

const sendMessage = async () => {
  if (!inputMessage.value.trim() || isLoading.value) return

  const userMessage = {
    role: 'user',
    content: inputMessage.value.trim(),
    timestamp: new Date()
  }

  messages.value.push(userMessage)
  const userMessageText = inputMessage.value.trim()
  inputMessage.value = ''
  isLoading.value = true

  if (textareaRef.value) {
    textareaRef.value.style.height = 'auto'
  }

  try {
    const response = await fetch('/api/widget/prompt_send_message', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        fingerprint: userId.value,
        temp_chat_id: tempChatId.value,
        user_message: userMessageText,
        platform: selectedPlatform.value,
        style: selectedStyle.value,
        ratio: selectedRatio.value
      })
    })

    const data = await response.json()

    if (!response.ok) {
      if (data.should_register) {
        isLoading.value = false
        messages.value.push({
          role: 'assistant',
          content: data.error || 'Вы достигли лимита сообщений. Пожалуйста, зарегистрируйтесь, чтобы продолжить.',
          timestamp: new Date()
        })
        return
      }
      throw new Error(data.error || 'Failed to send message')
    }

    tempChatId.value = data.temp_chat_id

  } catch (error) {
    console.error('Error sending message:', error)
    isLoading.value = false
    messages.value.push({
      role: 'assistant',
      content: 'Произошла ошибка при генерации промпта. Попробуйте еще раз.',
      timestamp: new Date()
    })
  }
}

const loadChatHistory = async () => {
  if (!userId.value) return

  try {
    const response = await fetch(`/api/widget/prompt_load_chat?fingerprint=${userId.value}`)

    if (!response.ok) {
      throw new Error('Failed to load chat history')
    }

    const data = await response.json()
    tempChatId.value = data.temp_chat_id

    if (data.messages && data.messages.length > 0) {
      messages.value = data.messages.map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.text,
        timestamp: new Date()
      }))

      nextTick(() => {
        if (messagesContainer.value) {
          messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
        }
      })
    }
  } catch (error) {
    console.error('Error loading chat history:', error)
  }
}

const clearChat = async () => {
  if (typingIntervalId.value) {
    clearInterval(typingIntervalId.value)
    typingIntervalId.value = null
  }

  if (tempChatId.value) {
    try {
      await fetch('/api/widget/prompt_close_chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fingerprint: userId.value,
          temp_chat_id: tempChatId.value
        })
      })
    } catch (error) {
      console.error('Error closing chat:', error)
    }
  }

  messages.value = []
  tempChatId.value = null
  inputMessage.value = ''
  isLoading.value = false
  if (textareaRef.value) {
    textareaRef.value.style.height = 'auto'
  }
}

onMounted(async () => {
  try {
    userId.value = await getUserFingerprint()
    console.log('User ID:', userId.value)

    await loadChatHistory()
  } catch (error) {
    console.error('Failed to get user fingerprint:', error)
  } finally {
    isInitializing.value = false

    nextTick(() => {
      if (messagesContainer.value && messages.value.length > 0) {
        messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
      }
    })
  }

  if (textareaRef.value) {
    textareaRef.value.focus()
  }
})

onUnmounted(() => {
  if (typingIntervalId.value) {
    clearInterval(typingIntervalId.value)
    typingIntervalId.value = null
  }

  if (subscription.value) {
    subscription.value.unsubscribe()
    subscription.value = null
  }
  if (cable.value) {
    cable.value.disconnect()
    cable.value = null
  }
})
</script>

<style scoped>
textarea {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  line-height: 1.5;
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-4px);
  }
}

.animate-bounce {
  animation: bounce 1s ease-in-out infinite;
}
</style>
